<script setup lang="ts" generic="T extends string | number = string">
import { PV } from "@/widgets";
import type { AutoCompleteCompleteEvent, AutoCompleteOptionSelectEvent } from "primevue";
import { computed, ref, watchEffect } from "vue";
import type { CompleteParam } from "../../common/types.common";
import { defineActions, defineState } from "../common";
import { Buttons } from "../icons";
import { FormExt, FormGuide, FormHead, FormMessage } from "../internal";
import { type BaseProps, defaultProps } from "../props";

type OptionFn = (param?: CompleteParam) => Promise<T[]>;

type ModelType = NonNullable<T>;
interface PropsType extends BaseProps<ModelType> {
    options: OptionFn | T[];
    formId?: string;
    strict?: boolean;
    placeholder?: string;
}

const props = withDefaults(defineProps<PropsType>(), {
    ...defaultProps<ModelType>(),
    value: undefined,
});

const slots = defineSlots<{
    icon?: () => unknown;
}>();

const model = defineModel<ModelType | undefined>({
    default: undefined,
});

const state = defineState({
    model: model,
    props: props,
    hasValue: () => model.value !== undefined,
});

const actions = defineActions(model, props, state);

const choiceList = ref<T[]>([]);
const virtualScrollerOptions = computed(() => (choiceList.value.length > 20 ? { itemSize: 42 } : undefined));
const showLoading = ref(false);

watchEffect(() => {
    if (model.value === "" || model.value === null) {
        model.value = undefined;
    }
});

async function populateOptions(event: AutoCompleteCompleteEvent) {
    let data: T[];

    if (props.options instanceof Function) {
        showLoading.value = true;
        data = await props.options({ query: event.query });
        showLoading.value = false;
    } else {
        data = props.options;
    }

    choiceList.value = data;
}

function selectOption(event: AutoCompleteOptionSelectEvent) {
    model.value = event.value;
}
</script>

<template>
    <div class="flex flex-col gap-1">
        <FormHead :actions="actions" :param="props" :state="state" />

        <template v-if="!state.editMode">
            <PV.IconField>
                <PV.InputIcon v-if="slots.icon !== undefined" class="-mt-2.5!">
                    <slot name="icon"></slot>
                </PV.InputIcon>
                <PV.InputText
                    class="rns-input-static"
                    disabled
                    fluid
                    :value="(model ?? props.placeholder ?? '').toString()"
                />
                <FormExt :actions="actions" :state="state" />
            </PV.IconField>
        </template>
        <template v-else>
            <PV.IconField>
                <PV.InputIcon v-if="slots.icon !== undefined" class="-mt-2.5!">
                    <slot name="icon"></slot>
                </PV.InputIcon>
                <PV.AutoComplete
                    v-model.trim="model"
                    :completeOnFocus="true"
                    fluid
                    :forceSelection="props.strict === true"
                    :form="props.formId"
                    :placeholder="props.placeholder ?? `Masukkan ${props.label}`"
                    :suggestions="choiceList"
                    :virtualScrollerOptions="virtualScrollerOptions"
                    @complete="populateOptions"
                    @optionSelect="selectOption"
                />
                <Buttons.ExtReset v-if="state.clearable && state.hasValue" @click="actions.onReset" />
            </PV.IconField>
        </template>

        <FormGuide :state="state" :text="props.guideText" />
        <FormMessage :state="state" />
    </div>
</template>

<style scoped>
@reference "@/styles/main.css";

.rns-input-static {
    @apply text-gray-700! bg-transparent! shadow-none! px-1! pt-2.25!;
    @apply rns-g-static-input;
}
</style>
